import { useState, useEffect } from 'react';
import { CreditCard, ExternalLink, Check, Zap } from 'lucide-react';
import { T } from '../theme';
import { TIER_NAMES, TIER_PRICES, TIER_ORDER } from '../config/tierDefaults';
import { DEFAULT_PRICING } from '../config/pricingDefaults';
import { startCheckout, openBillingPortal } from '../utils/billingStorage';
import { getPricingConfig } from '../utils/accountStorage';
import { trackBeginCheckout } from '../analytics';

const STATUS_LABELS = {
  active:    { label: 'Active',       color: '#16a34a', bg: '#f0fdf4' },
  trialing:  { label: 'Trial',        color: '#0A7C8A', bg: '#DFFAF7' },
  past_due:  { label: 'Payment due',  color: '#D97B28', bg: '#FEF4E3' },
  canceled:  { label: 'Canceled',     color: '#6B7280', bg: '#F9FAFB' },
  incomplete:{ label: 'Incomplete',   color: '#D97B28', bg: '#FEF4E3' },
};

function PlanRow({ planKey, isCurrent, onUpgrade, upgrading, planData }) {
  const isFree  = planKey === 'basic';
  const accent  = planKey === 'pro' ? T.amberDeep : T.tealDeep;
  const gradBg  = planKey === 'pro' ? T.gradAmber : T.gradTeal;

  return (
    <div style={{
      border: isCurrent ? `2px solid ${accent}` : '1.5px solid #E5E7EB',
      borderRadius: 14,
      padding: '16px',
      marginBottom: 10,
      background: isCurrent ? (planKey === 'pro' ? T.amberPale : T.tealPale) : '#fff',
      position: 'relative',
    }}>
      {isCurrent && (
        <div style={{
          position: 'absolute', top: -10, right: 14,
          background: accent, color: '#fff',
          fontSize: 10, fontWeight: 800, padding: '2px 10px', borderRadius: 10, letterSpacing: 0.5,
        }}>
          CURRENT PLAN
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{TIER_NAMES[planKey]}</div>
          <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{TIER_PRICES[planKey]}</div>
        </div>
        {!isCurrent && !isFree && (
          <button
            onClick={() => onUpgrade(planKey)}
            disabled={upgrading === planKey}
            style={{
              padding: '9px 18px', borderRadius: 10, border: 'none',
              background: upgrading === planKey ? '#9CA3AF' : gradBg,
              color: '#fff', fontSize: 13, fontWeight: 700,
              cursor: upgrading ? 'not-allowed' : 'pointer',
              minWidth: 90,
            }}
          >
            {upgrading === planKey ? 'Opening…' : `Upgrade`}
          </button>
        )}
        {isCurrent && !isFree && (
          <Check size={18} color={accent} />
        )}
      </div>

      {/* Top 3 features */}
      <div style={{ marginTop: 10 }}>
        {(planData?.features || []).slice(0, 3).map(f => (
          <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
            <div style={{ width: 14, height: 14, borderRadius: '50%', background: isCurrent ? accent : '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Check size={8} color={isCurrent ? '#fff' : '#9CA3AF'} />
            </div>
            <span style={{ fontSize: 12, color: '#4B5563' }}>{f}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BillingModal({ account, onClose }) {
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgrading, setUpgrading]         = useState(null);
  const [error, setError]                 = useState(null);
  const [pricing, setPricing]             = useState(DEFAULT_PRICING);

  useEffect(() => {
    getPricingConfig().then(remote => {
      if (!remote) return;
      const merged = {};
      for (const k of Object.keys(DEFAULT_PRICING)) {
        merged[k] = { ...DEFAULT_PRICING[k], ...(remote[k] || {}) };
      }
      setPricing(merged);
    });
  }, []);

  const tier               = account?.tier || 'basic';
  const status             = account?.subscriptionStatus;
  const periodEnd          = account?.currentPeriodEnd;
  const hasSubscription    = !!account?.stripeCustomerId && tier !== 'basic';
  const statusInfo         = STATUS_LABELS[status] || null;

  const periodEndDate = periodEnd
    ? new Date(periodEnd * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  const handleUpgrade = async (planKey) => {
    setUpgrading(planKey);
    setError(null);
    try {
      trackBeginCheckout(planKey);
      await startCheckout(planKey); // redirects away — component unmounts
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Could not open checkout. Please try again.');
      setUpgrading(null);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      await openBillingPortal(); // redirects away
    } catch (err) {
      console.error('Portal error:', err);
      setError(err.message || 'Could not open billing portal. Please try again.');
      setPortalLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        zIndex: 1100, backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: '20px 20px 0 0',
          padding: '24px 20px 28px', width: '100%', maxWidth: 420,
          maxHeight: '90vh', overflowY: 'auto',
          animation: 'slideUp 0.3s ease-out',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CreditCard size={20} color={T.tealDeep} />
            <span style={{ fontSize: 18, fontWeight: 800, color: '#111' }}>Plan & Billing</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
        </div>

        {/* Current plan status */}
        <div style={{
          background: T.gradNavy, borderRadius: 16, padding: '18px 20px', marginBottom: 20,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
            Current Plan
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#fff' }}>{TIER_NAMES[tier]}</div>
            {statusInfo && (
              <span style={{
                fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 10,
                background: statusInfo.bg, color: statusInfo.color, letterSpacing: 0.3,
              }}>
                {statusInfo.label}
              </span>
            )}
          </div>
          {periodEndDate && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 6 }}>
              {status === 'canceled' ? 'Access until' : 'Renews'} {periodEndDate}
            </div>
          )}
          {tier === 'basic' && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
              Free forever · No credit card required
            </div>
          )}
        </div>

        {/* Past-due warning */}
        {status === 'past_due' && (
          <div style={{
            background: '#FEF4E3', border: '1.5px solid #F2A93B',
            borderRadius: 12, padding: '12px 14px', marginBottom: 16,
            fontSize: 13, color: '#92400E', fontWeight: 600,
          }}>
            ⚠ Your last payment failed. Update your payment method to keep your plan active.
          </div>
        )}

        {/* Manage billing portal (existing subscribers) */}
        {hasSubscription && (
          <button
            onClick={handlePortal}
            disabled={portalLoading}
            style={{
              width: '100%', padding: '13px', borderRadius: 12, border: `1.5px solid ${T.tealDeep}`,
              background: '#fff', color: T.tealDeep, fontSize: 14, fontWeight: 700,
              cursor: portalLoading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              marginBottom: 20, opacity: portalLoading ? 0.6 : 1,
            }}
          >
            <ExternalLink size={15} />
            {portalLoading ? 'Opening portal…' : 'Manage Billing'}
          </button>
        )}

        {/* Plan comparison */}
        <div style={{ marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
            <Zap size={14} color={T.tealDeep} />
            <span style={{ fontSize: 13, fontWeight: 800, color: '#374151' }}>
              {tier === 'basic' ? 'Choose a plan' : 'Available plans'}
            </span>
          </div>
          {TIER_ORDER.filter(k =>
            !hasSubscription || TIER_ORDER.indexOf(k) >= TIER_ORDER.indexOf(tier)
          ).map(k => (
            <PlanRow
              key={k}
              planKey={k}
              isCurrent={k === tier}
              onUpgrade={handleUpgrade}
              upgrading={upgrading}
              planData={pricing[k]}
            />
          ))}
        </div>

        {error && (
          <div style={{
            background: '#FEF2F2', border: '1.5px solid #FCA5A5',
            borderRadius: 10, padding: '10px 12px', fontSize: 12,
            color: '#DC2626', fontWeight: 600, marginTop: 12,
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default BillingModal;
